import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { EffectFade } from 'swiper/modules';

type FeatureItem = {
    id: string;
    title: string;
    titleImg: string;
    description: string;
    image: string;
    thumbnail: string;
};

export type PreregisterFeaturesSectionHandle = {
    handleWheel: (deltaY: number, target: EventTarget | null) => boolean;
    isAtLastSlide: () => boolean;
    isAtFirstSlide: () => boolean;
};

type PreregisterFeaturesSectionProps = {
    isMobileDevice: boolean;
    isActive: boolean;
    onMoveSectionByDelta: (delta: number) => void;
    onReachBeyondLast?: () => void;
};

const SWIPER_SPEED_MS = 760;
const TRANSITION_DELAY_MS = 500;
const AUTO_DELAY_MS = 15000;
const MANUAL_GUARD_MS = 900;
const PreregisterFeaturesSection = forwardRef<PreregisterFeaturesSectionHandle, PreregisterFeaturesSectionProps>(({
    isMobileDevice,
    isActive,
    onMoveSectionByDelta,
    onReachBeyondLast,
}: PreregisterFeaturesSectionProps, ref) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const featureSwiperRef = useRef<SwiperType | null>(null);
    const featureVideoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const pendingSlideTimeoutRef = useRef<number | null>(null);
    const queuedWheelDeltaRef = useRef<-1 | 0 | 1>(0);
    const queuedWheelTimerRef = useRef<number | null>(null);
    const sectionAnimTimerRef = useRef<number | null>(null);
    const actionLockedUntilRef = useRef(0);
    const autoPauseUntilRef = useRef(0);
    const [isMobileFeatureActive, setIsMobileFeatureActive] = useState(false);
    const effectiveIsActive = isMobileDevice ? isMobileFeatureActive : isActive;
    const prevIsActiveRef = useRef(effectiveIsActive);

    const [activeFeature, setActiveFeature] = useState(0);
    const [sectionEnterSeq, setSectionEnterSeq] = useState(0);
    const [isSectionAnimReady, setIsSectionAnimReady] = useState(false);

    const gameFeatures = useMemo<FeatureItem[]>(() => [
        {
            id: 'DUMMY 01',
            title: 'DNA',
            titleImg: '/images/evnt/grandopen/slideTitl01.png',
            description: `베어브릭들의 몸 속에는 단 하나뿐인 ‘원형 DNA’ 가 새겨져 있습니다. <br />유저가 직접 선택한 코드는 수천 가지 변이 가능성을 품고 태어나며,<br />그 조합은  두 번 다시 같은 모습으로 나타나지 않는 희귀한 개체성의 증표가 됩니다.`,
            image: 'https://bb-cdn.onuniverse.io/web/event/grandopen/0888d512-905c-4940-8f81-45263c4bce6a.mp4',
            thumbnail: '',
        },
        {
            id: 'DUMMY 02',
            title: '직업',
            titleImg: '/images/evnt/grandopen/slideTitl02.png',
            description: `베어브릭들은 혹독한 환경에서 살아남기 위해<br />각기 다른 전장의 역할을 수행하도록 만들어졌습니다.<br /> 최전선을 버티는 개체도 , 빠르게 적을 제압하는 개체도,<br />원거리에서 넓은 공격을 펼치거나 동료를 지원하는 개체도 존재합니다 .`,
            image: 'https://bb-cdn.onuniverse.io/web/event/grandopen/ea2587a8-d3ca-44d4-89f3-bb3e1423faed.mp4',
            thumbnail: '',
        },
        {
            id: 'DUMMY 03',
            title: '전투',
            titleImg: '/images/evnt/grandopen/slideTitl03.png',
            description: `베어브릭들이 헤쳐 나가는 세계에는 여러 위협이 잠들어 있습니다.<br />  
넓은 필드에서의 작은 충돌부터 1 인 던전의 폐허,<br />
예고 없이 나타나는 정예 개체와 지역을 뒤흔드는 거대한 보스까지<br />
이 모든 경험은 베어브릭들에게 새로운 정보를 남기며 그들은 그렇게 한 걸음씩 강해져 갑니다.`,
            image: 'https://bb-cdn.onuniverse.io/web/event/grandopen/c21047d2-c756-4d42-8915-e0638130331d.mp4',
            thumbnail: '',
        },
        {
            id: 'DUMMY 04',
            title: '생활',
            titleImg: '/images/evnt/grandopen/slideTitl04.png',
            description: `이 세계에서 자원은 결코 넉넉하지 않습니다.<br />  
대부분의 생명체들은 흩어진 잔해를 모아 장비를 보완하고,<br />  
남은 에너지를 정제해 하루를 버팁니다.<br />  
그렇게 무너진 세상 속에서도 조용히 내일을 이어가는 작은 삶을 만들어 갑니다.`,
            image: 'https://bb-cdn.onuniverse.io/web/event/grandopen/300e7ec3-3654-477b-8b47-4e78513fd197.mp4',
            thumbnail: '',
        },
    ], []);

    const clearPendingSlideTimeout = useCallback(() => {
        if (pendingSlideTimeoutRef.current != null) {
            window.clearTimeout(pendingSlideTimeoutRef.current);
            pendingSlideTimeoutRef.current = null;
        }
    }, []);

    const clearQueuedWheelTimer = useCallback(() => {
        if (queuedWheelTimerRef.current != null) {
            window.clearTimeout(queuedWheelTimerRef.current);
            queuedWheelTimerRef.current = null;
        }
    }, []);

    const clearSectionAnimTimer = useCallback(() => {
        if (sectionAnimTimerRef.current != null) {
            window.clearTimeout(sectionAnimTimerRef.current);
            sectionAnimTimerRef.current = null;
        }
    }, []);

    const isActionLocked = useCallback(() => Date.now() < actionLockedUntilRef.current, []);

    const resetAutoCycle = useCallback(() => {
        autoPauseUntilRef.current = Date.now() + TRANSITION_DELAY_MS;
    }, []);

    const applyManualGuard = useCallback(() => {
        autoPauseUntilRef.current = Date.now() + MANUAL_GUARD_MS;
    }, []);

    const scheduleSlideTo = useCallback((targetIndex: number, delayMs = 0) => {
        const swiper = featureSwiperRef.current;
        if (!swiper || swiper.destroyed) {
            setActiveFeature(targetIndex);
            return true;
        }

        if (isActionLocked()) return false;

        clearPendingSlideTimeout();
        actionLockedUntilRef.current = Date.now() + delayMs + SWIPER_SPEED_MS + 120;

        const moveToTarget = () => {
            if (isMobileDevice && swiper.params.loop) {
                swiper.slideToLoop(targetIndex);
                return;
            }
            swiper.slideTo(targetIndex);
        };

        if (delayMs <= 0) {
            moveToTarget();
            return true;
        }

        pendingSlideTimeoutRef.current = window.setTimeout(() => {
            pendingSlideTimeoutRef.current = null;
            moveToTarget();
        }, delayMs);

        return true;
    }, [clearPendingSlideTimeout, isActionLocked, isMobileDevice]);

    const getCurrentIndex = useCallback(() => {
        const swiper = featureSwiperRef.current;
        if (!swiper || swiper.destroyed) return activeFeature;
        if (isMobileDevice) return swiper.realIndex;
        return swiper.activeIndex;
    }, [activeFeature, isMobileDevice]);

    const requestPrev = useCallback(() => {
        applyManualGuard();
        const current = getCurrentIndex();

        if (current <= 0) {
            return scheduleSlideTo(gameFeatures.length - 1, 0);
        }

        return scheduleSlideTo(current - 1, 0);
    }, [applyManualGuard, gameFeatures.length, getCurrentIndex, scheduleSlideTo]);

    const requestPrevFromWheel = useCallback(() => {
        applyManualGuard();
        const current = getCurrentIndex();

        if (current <= 0) {
            clearPendingSlideTimeout();
            onMoveSectionByDelta(-1);
            return true;
        }

        return scheduleSlideTo(current - 1, 0);
    }, [applyManualGuard, clearPendingSlideTimeout, getCurrentIndex, onMoveSectionByDelta, scheduleSlideTo]);

    const requestNext = useCallback(() => {
        applyManualGuard();
        const current = getCurrentIndex();

        if (current >= gameFeatures.length - 1) {
            return scheduleSlideTo(0, 0);
        }

        return scheduleSlideTo(current + 1, 0);
    }, [applyManualGuard, gameFeatures.length, getCurrentIndex, scheduleSlideTo]);

    const requestNextFromWheel = useCallback(() => {
        applyManualGuard();
        const current = getCurrentIndex();

        if (current >= gameFeatures.length - 1) {
            clearPendingSlideTimeout();
            if (onReachBeyondLast) {
                onReachBeyondLast();
                return true;
            }
            onMoveSectionByDelta(1);
            return true;
        }

        return scheduleSlideTo(current + 1, 0);
    }, [applyManualGuard, clearPendingSlideTimeout, gameFeatures.length, getCurrentIndex, onMoveSectionByDelta, onReachBeyondLast, scheduleSlideTo]);

    const requestMoveTo = useCallback((index: number) => {
        applyManualGuard();
        return scheduleSlideTo(index, 0);
    }, [applyManualGuard, scheduleSlideTo]);

    const runQueuedWheel = useCallback(() => {
        const queued = queuedWheelDeltaRef.current;
        queuedWheelDeltaRef.current = 0;
        clearQueuedWheelTimer();

        if (queued === 0) return;
        if (queued > 0) {
            requestNextFromWheel();
            return;
        }
        requestPrevFromWheel();
    }, [clearQueuedWheelTimer, requestNextFromWheel, requestPrevFromWheel]);

    useEffect(() => {
        return () => {
            clearPendingSlideTimeout();
            clearQueuedWheelTimer();
            clearSectionAnimTimer();
        };
    }, [clearPendingSlideTimeout, clearQueuedWheelTimer, clearSectionAnimTimer]);

    useEffect(() => {
        if (!isMobileDevice) {
            setIsMobileFeatureActive(false);
            return;
        }

        const sectionEl = sectionRef.current;
        if (!sectionEl) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (!entry) return;

                setIsMobileFeatureActive(entry.isIntersecting && entry.intersectionRatio >= 0.2);
            },
            {
                threshold: [0.1, 0.2, 0.35, 0.5],
                rootMargin: '-10% 0px -10% 0px',
            }
        );

        observer.observe(sectionEl);
        return () => observer.disconnect();
    }, [isMobileDevice]);

    useEffect(() => {
        if (!effectiveIsActive) return;
        resetAutoCycle();
    }, [activeFeature, effectiveIsActive, resetAutoCycle]);

    useEffect(() => {
        const wasActive = prevIsActiveRef.current;

        if (effectiveIsActive && !wasActive) {
            setIsSectionAnimReady(false);
            clearSectionAnimTimer();
            sectionAnimTimerRef.current = window.setTimeout(() => {
                setSectionEnterSeq((prev) => prev + 1);
                setIsSectionAnimReady(true);
                sectionAnimTimerRef.current = null;
            }, Math.max(120, TRANSITION_DELAY_MS - 140));
        }

        if (!effectiveIsActive && wasActive) {
            clearPendingSlideTimeout();
            clearQueuedWheelTimer();
            clearSectionAnimTimer();
            queuedWheelDeltaRef.current = 0;
            actionLockedUntilRef.current = 0;
            autoPauseUntilRef.current = 0;
            setIsSectionAnimReady(false);

            const swiper = featureSwiperRef.current;
            if (!isMobileDevice && swiper && !swiper.destroyed) {
                swiper.slideTo(0, 0);
                setActiveFeature(0);
            }
        }

        prevIsActiveRef.current = effectiveIsActive;
    }, [effectiveIsActive, isMobileDevice, clearPendingSlideTimeout, clearQueuedWheelTimer, clearSectionAnimTimer]);

    useEffect(() => {
        if (!effectiveIsActive) return;

        const autoId = window.setInterval(() => {
            if (isActionLocked()) return;
            const now = Date.now();
            if (now < autoPauseUntilRef.current + AUTO_DELAY_MS) return;

            const current = getCurrentIndex();
            const next = (current + 1) % gameFeatures.length;
            scheduleSlideTo(next, 0);
            autoPauseUntilRef.current = Number.MAX_SAFE_INTEGER;
        }, 120);

        return () => window.clearInterval(autoId);
    }, [effectiveIsActive, gameFeatures.length, getCurrentIndex, isActionLocked, scheduleSlideTo]);

    useEffect(() => {
        if (/iPad|iPhone|iPod/.test(navigator.userAgent) && typeof (window as any).MSStream === 'undefined') {
            featureVideoRefs.current.forEach((video: HTMLVideoElement | null) => {
                if (video && video.paused) {
                    video.play().catch(() => {});
                }
            });
        }
    }, [activeFeature]);

    useImperativeHandle(ref, () => ({
        handleWheel: (deltaY: number, _target: EventTarget | null) => {
            if (isMobileDevice) return false;
            if (!effectiveIsActive) return false;
            if (deltaY === 0) return true;

            if (isActionLocked()) {
                queuedWheelDeltaRef.current = deltaY > 0 ? 1 : -1;
                if (queuedWheelTimerRef.current == null) {
                    const remainMs = Math.max(16, actionLockedUntilRef.current - Date.now() + 20);
                    queuedWheelTimerRef.current = window.setTimeout(() => {
                        runQueuedWheel();
                    }, remainMs);
                }
                return true;
            }

            if (deltaY > 0) {
                requestNextFromWheel();
                return true;
            }

            requestPrevFromWheel();
            return true;
        },
        isAtLastSlide: () => getCurrentIndex() >= gameFeatures.length - 1,
        isAtFirstSlide: () => getCurrentIndex() <= 0,
    }), [effectiveIsActive, gameFeatures.length, getCurrentIndex, isActionLocked, isMobileDevice, requestNextFromWheel, requestPrevFromWheel, runQueuedWheel]);

    return (
        <div className='gameIntroSec' id='featuresSec' ref={sectionRef}>
            <div className={`game-features${effectiveIsActive ? ' is-section-active' : ''}${isSectionAnimReady ? ' is-anim-ready' : ''}`}>
                <div className="features-horizontal-wrap">
                    <Swiper
                        className="features-horizontal-swiper"
                        modules={isMobileDevice ? [] : [EffectFade]}
                        slidesPerView={1}
                        loop={isMobileDevice}
                        speed={SWIPER_SPEED_MS}
                        effect={isMobileDevice ? 'slide' : 'fade'}
                        fadeEffect={isMobileDevice ? undefined : { crossFade: true }}
                        observer={true}
                        observeParents={true}
                        watchSlidesProgress={true}
                        allowTouchMove={true}
                        nested={true}
                        onSwiper={(swiper: SwiperType) => {
                            featureSwiperRef.current = swiper;
                            if (isMobileDevice && swiper.params.loop) {
                                swiper.slideToLoop(activeFeature, 0);
                                return;
                            }
                            swiper.slideTo(activeFeature, 0);
                        }}
                        onSlideChange={(swiper: SwiperType) => {
                            const nextIndex = (isMobileDevice && swiper.params.loop)
                                ? swiper.realIndex
                                : swiper.activeIndex;

                            if (nextIndex !== activeFeature) {
                                setActiveFeature(nextIndex);
                            }
                        }}
                        onRealIndexChange={(swiper: SwiperType) => {
                            if (!isMobileDevice || !swiper.params.loop) return;
                            if (swiper.realIndex !== activeFeature) {
                                setActiveFeature(swiper.realIndex);
                            }
                        }}
                    >
                        {gameFeatures.map((feature, index) => {
                            const isCurrentSlide = index === activeFeature;

                            return (
                            <SwiperSlide key={feature.id}>
                                <div className="feature-horizontal-slide">
                                    <div className="feature-horizontal-media">
                                        <video
                                            ref={(el) => { featureVideoRefs.current[index] = el; }}
                                            src={feature.image}
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            className="feature-horizontal-video"
                                        />
                                        <div className="feature-horizontal-video-overlay"></div>
                                    </div>
                                    <div className="feature-horizontal-overlay">
                                        <div key={`overlay-enter-${sectionEnterSeq}`}>
                                        <img
                                            src={feature.titleImg}
                                            alt={feature.title}
                                            className={`feature-horizontal-thumbnail${isCurrentSlide ? ' is-current' : ''}`}
                                        />
                                        <div className={`feature-horizontal-description${isCurrentSlide ? ' is-current' : ''}`}>
                                            <div className="feature-horizontal-description-overlay"></div>
                                            <p dangerouslySetInnerHTML={{ __html: feature.description }}></p>
                                        </div>
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                            );
                        })}
                    </Swiper>
                    <button
                        type="button"
                        className="feature-nav-btn feature-nav-btn-arrow feature-nav-btn-prev"
                        onClick={requestPrev}
                        aria-label="이전 슬라이드"
                    >
                        <img className="feature-nav-arrow-icon" src={process.env.PUBLIC_URL + '/images/evnt/grandopen/slideBtn.png'} alt="" aria-hidden="true" />
                    </button>
                    <div className="feature-horizontal-pagination" aria-label="게임 소개 슬라이드 선택">
                        <div
                            className="feature-timeline"
                            role="tablist"
                            aria-label="게임 소개 타임라인"
                            style={{
                                ['--feature-count' as string]: String(gameFeatures.length),
                            }}
                        >
                            {gameFeatures.map((feature, index) => {
                                const isCurrent = index === activeFeature;
                                const isPast = index < activeFeature;
                                return (
                                    <button
                                        key={feature.id}
                                        type="button"
                                        className={[
                                            'feature-timeline-item',
                                            isCurrent ? 'is-active' : '',
                                            isPast ? 'is-past' : '',
                                        ].join(' ').trim()}
                                        onClick={() => {
                                            requestMoveTo(index);
                                        }}
                                        role="tab"
                                        aria-selected={isCurrent}
                                        aria-label={`${feature.title} 보기`}
                                    >
                                        <span className="feature-timeline-dot" aria-hidden="true"></span>
                                        <span className="feature-timeline-label">{feature.title}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <button
                        type="button"
                        className="feature-nav-btn feature-nav-btn-arrow feature-nav-btn-next"
                        onClick={requestNext}
                        aria-label="다음 슬라이드"
                    >
                        <img className="feature-nav-arrow-icon" src={process.env.PUBLIC_URL + '/images/evnt/grandopen/slideBtn.png'} alt="" aria-hidden="true" />
                    </button>
                </div>
            </div>
        </div>
    );
});

PreregisterFeaturesSection.displayName = 'PreregisterFeaturesSection';

export default PreregisterFeaturesSection;
